/**
 * Comprehensive Form Validation Utilities
 * Provides real-time validation with interactive feedback
 */

/**
 * Email validation
 * @param {string} email - Email to validate
 * @returns {Object} - Validation result with message
 */
export const validateEmail = (email) => {
  if (!email) {
    return { isValid: false, message: 'Email is required' };
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return { isValid: false, message: 'Please enter a valid email address' };
  }

  return { isValid: true, message: '' };
};

/**
 * Phone number validation - World-class validation with country code support
 * @param {string} phone - Phone number to validate (international format with + prefix)
 * @returns {Object} - Validation result with message
 */
export const validatePhone = (phone) => {
  if (!phone || !phone.trim()) {
    return { isValid: false, message: 'Phone number is required' };
  }

  const trimmedPhone = phone.trim();

  // Must start with + for international format
  if (!trimmedPhone.startsWith('+')) {
    return {
      isValid: false,
      message: 'Phone number must include country code. Format: +[country code][number] (e.g., +919876543210, +12025551234)'
    };
  }

  // Remove + and validate digits
  const digitsOnly = trimmedPhone.substring(1).replace(/\D/g, '');

  // Check if it has enough digits (minimum 10 digits after country code)


  // Maximum 15 digits total (including country code) per ITU-T E.164
  if (digitsOnly.length > 15) {
    return {
      isValid: false,
      message: 'Phone number is too long. Maximum 15 digits allowed (including country code)'
    };
  }

  // Basic country code validation (must be 1-3 digits)
  const countryCodeMatch = trimmedPhone.match(/^\+(1\d{0,2}|\d{1,3})/);
  if (!countryCodeMatch) {
    return {
      isValid: false,
      message: 'Invalid country code. Format: +[country code][number]'
    };
  }

  // Additional validation: ensure valid country code range (1-999)
  const countryCode = parseInt(digitsOnly.substring(0, Math.min(3, digitsOnly.length)), 10);
  if (countryCode < 1 || countryCode > 999) {
    return {
      isValid: false,
      message: 'Invalid country code. Please use a valid international country code'
    };
  }

  return { isValid: true, message: '' };
};




/**
 * Name validation
 * @param {string} name - Name to validate
 * @param {string} fieldName - Field name for error message
 * @returns {Object} - Validation result with message
 */
export const validateName = (name, fieldName = 'Name') => {
  if (!name || !name.trim()) {
    return { isValid: false, message: `${fieldName} is required` };
  }

  if (name.trim().length < 2) {
    return { isValid: false, message: `${fieldName} must be at least 2 characters` };
  }

  if (name.trim().length > 100) {
    return { isValid: false, message: `${fieldName} cannot exceed 100 characters` };
  }

  // Check for valid characters (letters, spaces, hyphens, apostrophes)
  const nameRegex = /^[a-zA-Z\s\-']+$/;
  if (!nameRegex.test(name.trim())) {
    return { isValid: false, message: `${fieldName} can only contain letters, spaces, hyphens, and apostrophes` };
  }

  return { isValid: true, message: '' };
};

/**
 * Password validation
 * @param {string} password - Password to validate
 * @returns {Object} - Validation result with message
 */
export const validatePassword = (password) => {
  if (!password) {
    return { isValid: false, message: 'Password is required' };
  }

  if (password.length < 8) {
    return { isValid: false, message: 'Password must be at least 8 characters' };
  }

  if (password.length > 128) {
    return { isValid: false, message: 'Password cannot exceed 128 characters' };
  }

  // Check for at least one uppercase letter
  if (!/[A-Z]/.test(password)) {
    return { isValid: false, message: 'Password must contain at least one uppercase letter' };
  }

  // Check for at least one lowercase letter
  if (!/[a-z]/.test(password)) {
    return { isValid: false, message: 'Password must contain at least one lowercase letter' };
  }

  // Check for at least one number
  if (!/\d/.test(password)) {
    return { isValid: false, message: 'Password must contain at least one number' };
  }

  return { isValid: true, message: '' };
};

/**
 * Meeting title validation
 * @param {string} title - Meeting title to validate
 * @returns {Object} - Validation result with message
 */
export const validateMeetingTitle = (title) => {
  if (!title || !title.trim()) {
    return { isValid: false, message: 'Meeting title is required' };
  }

  if (title.trim().length < 3) {
    return { isValid: false, message: 'Meeting title must be at least 3 characters' };
  }

  if (title.trim().length > 200) {
    return { isValid: false, message: 'Meeting title cannot exceed 200 characters' };
  }

  return { isValid: true, message: '' };
};

/**
 * Location validation
 * @param {string} location - Meeting location to validate
 * @returns {Object} - Validation result with message
 */
export const validateLocation = (location) => {
  if (!location || !location.trim()) {
    return { isValid: false, message: 'Meeting location is required' };
  }

  if (location.trim().length > 100) {
    return { isValid: false, message: 'Location cannot exceed 100 characters' };
  }

  return { isValid: true, message: '' };
};

/**
 * Meeting type validation
 * @param {string} type - Meeting type to validate
 * @returns {Object} - Validation result with message
 */
export const validateMeetingType = (type) => {
  if (!type) {
    return { isValid: false, message: 'Meeting type is required' };
  }

  // Accept both formats: lowercase (from backend) and title case (legacy)
  const validTypes = ['in-person', 'online', 'In-Person', 'Online'];
  if (!validTypes.includes(type)) {
    return { isValid: false, message: "Invalid meeting type. Must be 'in-person' or 'online'." };
  }

  return { isValid: true, message: '' };
};

/**
 * Date validation
 * @param {string} date - Date to validate (YYYY-MM-DD format)
 * @param {string} fieldName - Field name for error message
 * @returns {Object} - Validation result with message
 */
export const validateDate = (date, fieldName = 'Date') => {
  if (!date) {
    return { isValid: false, message: `${fieldName} is required` };
  }

  const dateObj = new Date(date);
  const now = new Date();

  if (isNaN(dateObj.getTime())) {
    return { isValid: false, message: 'Please enter a valid date' };
  }

  // Reset time to start of day for comparison
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const selectedDate = new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate());

  if (selectedDate < today) {
    return { isValid: false, message: `${fieldName} cannot be in the past` };
  }

  return { isValid: true, message: '' };
};

/**
 * Time validation
 * @param {string} time - Time to validate (HH:MM format)
 * @param {string} fieldName - Field name for error message
 * @returns {Object} - Validation result with message
 */
export const validateTime = (time, fieldName = 'Time') => {
  if (!time) {
    return { isValid: false, message: `${fieldName} is required` };
  }

  const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
  if (!timeRegex.test(time)) {
    return { isValid: false, message: 'Please enter a valid time (HH:MM format)' };
  }

  return { isValid: true, message: '' };
};

/**
 * Time range validation
 * @param {string} startTime - Start time (HH:MM format)
 * @param {string} endTime - End time (HH:MM format)
 * @returns {Object} - Validation result with message
 */
export const validateTimeRange = (startTime, endTime) => {
  const startValidation = validateTime(startTime, 'Start time');
  if (!startValidation.isValid) {
    return startValidation;
  }

  const endValidation = validateTime(endTime, 'End time');
  if (!endValidation.isValid) {
    return endValidation;
  }

  if (startTime >= endTime) {
    return { isValid: false, message: 'End time must be after start time' };
  }

  // Check if meeting duration is reasonable (at least 15 minutes, max 8 hours)
  const startMinutes = timeToMinutes(startTime);
  const endMinutes = timeToMinutes(endTime);
  const duration = endMinutes - startMinutes;

  if (duration < 15) {
    return { isValid: false, message: 'Meeting duration must be at least 15 minutes' };
  }

  if (duration > 480) { // 8 hours
    return { isValid: false, message: 'Meeting duration cannot exceed 8 hours' };
  }

  return { isValid: true, message: '' };
};

/**
 * Individual time field validation (for Start Time field)
 * @param {string} startTime - Start time
 * @param {string} endTime - End time (for checking range)
 * @param {string} scheduledDate - Optional scheduled date to check if time is in the past for today
 * @returns {Object} - Validation result with message
 */
export const validateStartTime = (startTime, endTime = '', scheduledDate = '') => {
  if (!startTime) {
    return { isValid: false, message: 'Start time is required' };
  }

  const timeValidation = validateTime(startTime, 'Start time');
  if (!timeValidation.isValid) {
    return timeValidation;
  }

  // Check if time is in the past when date is today
  if (scheduledDate) {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const selectedDate = new Date(scheduledDate);
    const selectedDateOnly = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());

    if (selectedDateOnly.getTime() === today.getTime()) {
      const currentTimeStr = now.toTimeString().slice(0, 5); // Get HH:MM format
      if (startTime < currentTimeStr) {
        return { isValid: false, message: 'Meeting time has already passed. Please select a future time.' };
      }
    }
  }

  // Don't validate range here - let validateEndTime handle it
  // This prevents showing the error on start time field when end time is the issue
  return { isValid: true, message: '' };
};

/**
 * Individual time field validation (for End Time field)
 * @param {string} endTime - End time
 * @param {string} startTime - Start time (for checking range)
 * @returns {Object} - Validation result with message
 */
export const validateEndTime = (endTime, startTime = '') => {
  if (!endTime) {
    return { isValid: false, message: 'End time is required' };
  }

  const timeValidation = validateTime(endTime, 'End time');
  if (!timeValidation.isValid) {
    return timeValidation;
  }

  // Only validate range if start time is also provided
  if (startTime && startTime >= endTime) {
    return { isValid: false, message: 'End time must be after start time' };
  }

  return { isValid: true, message: '' };
};

/**
 * Convert time to minutes for calculations
 * @param {string} time - Time in HH:MM format
 * @returns {number} - Minutes since midnight
 */
const timeToMinutes = (time) => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

/**
 * Participants validation
 * @param {Array} participants - Array of participant IDs
 * @returns {Object} - Validation result with message
 */
export const validateParticipants = (participants) => {
  if (!participants || participants.length === 0) {
    return { isValid: false, message: 'At least one participant is required' };
  }

  if (participants.length > 50) {
    return { isValid: false, message: 'Cannot have more than 50 participants' };
  }

  return { isValid: true, message: '' };
};

/**
 * Agenda validation
 * @param {string} agenda - Agenda text (HTML content from QuillEditor)
 * @returns {Object} - Validation result with message
 */
export const validateAgenda = (agenda) => {
  // Agenda is optional - no character limit
  return { isValid: true, message: '' };
};

/**
 * Action point description validation
 * @param {string} description - Action point description
 * @returns {Object} - Validation result with message
 */
export const validateActionPointDescription = (description) => {
  if (!description || !description.trim()) {
    return { isValid: false, message: 'Action point description is required' };
  }

  if (description.trim().length < 10) {
    return { isValid: false, message: 'Description must be at least 10 characters' };
  }

  if (description.trim().length > 1000) {
    return { isValid: false, message: 'Description cannot exceed 1000 characters' };
  }

  return { isValid: true, message: '' };
};

/**
 * Comment validation
 * @param {string} comment - Comment text
 * @param {string} fieldName - Field name for error message
 * @returns {Object} - Validation result with message
 */
export const validateComment = (comment, fieldName = 'Comment') => {
  if (!comment || !comment.trim()) {
    return { isValid: false, message: `${fieldName} is required` };
  }

  if (comment.trim().length < 5) {
    return { isValid: false, message: `${fieldName} must be at least 5 characters` };
  }

  if (comment.trim().length > 500) {
    return { isValid: false, message: `${fieldName} cannot exceed 500 characters` };
  }

  return { isValid: true, message: '' };
};

/**
 * File validation
 * @param {File} file - File to validate
 * @param {Array} allowedTypes - Allowed MIME types
 * @param {number} maxSize - Maximum file size in bytes
 * @returns {Object} - Validation result with message
 */
// Import centralized constants for consistency
const DEFAULT_MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB - consistent across all uploads

export const validateFile = (file, allowedTypes = [], maxSize = DEFAULT_MAX_FILE_SIZE) => {
  if (!file) {
    return { isValid: false, message: 'Please select a file' };
  }

  if (allowedTypes.length > 0 && !allowedTypes.includes(file.type)) {
    const allowedExtensions = allowedTypes.map(type => {
      const ext = type.split('/')[1];
      return ext ? `.${ext}` : '';
    }).filter(Boolean).join(', ');

    return {
      isValid: false,
      message: `File type not allowed. Allowed types: ${allowedExtensions}`
    };
  }

  if (file.size > maxSize) {
    const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(1);
    return {
      isValid: false,
      message: `File size cannot exceed ${maxSizeMB}MB`
    };
  }

  return { isValid: true, message: '' };
};

/**
 * OTP validation
 * @param {string} otp - OTP code
 * @returns {Object} - Validation result with message
 */
export const validateOTP = (otp) => {
  if (!otp) {
    return { isValid: false, message: 'OTP is required' };
  }

  const otpRegex = /^\d{6}$/;
  if (!otpRegex.test(otp)) {
    return { isValid: false, message: 'OTP must be 6 digits' };
  }

  return { isValid: true, message: '' };
};

/**
 * Role validation
 * @param {string} role - User role
 * @returns {Object} - Validation result with message
 */
export const validateRole = (role) => {
  const validRoles = ['admin', 'business_head', 'employee', 'vendor'];

  if (!role) {
    return { isValid: false, message: 'Role is required' };
  }

  if (!validRoles.includes(role)) {
    return { isValid: false, message: 'Please select a valid role' };
  }

  return { isValid: true, message: '' };
};

/**
 * Generic required field validation
 * @param {any} value - Value to validate
 * @param {string} fieldName - Field name for error message
 * @returns {Object} - Validation result with message
 */
export const validateRequired = (value, fieldName) => {
  if (!value || (typeof value === 'string' && !value.trim())) {
    return { isValid: false, message: `${fieldName} is required` };
  }

  return { isValid: true, message: '' };
};

/**
 * Get validation class for form fields
 * @param {boolean} isValid - Whether field is valid
 * @param {boolean} hasBeenTouched - Whether field has been touched
 * @returns {string} - CSS classes
 */
export const getValidationClasses = (isValid, hasBeenTouched) => {
  if (!hasBeenTouched) {
    return 'border-gray-300 focus:border-blue-500';
  }

  return isValid
    ? 'border-green-500 focus:border-green-500'
    : 'border-red-500 focus:border-red-500';
};

/**
 * Get validation icon
 * @param {boolean} isValid - Whether field is valid
 * @param {boolean} hasBeenTouched - Whether field has been touched
 * @returns {string|null} - Icon component or null
 */
export const getValidationIcon = (isValid, hasBeenTouched) => {
  if (!hasBeenTouched) return null;

  return isValid ? '✓' : '✗';
};
